import classNames from 'classnames';
import React from 'react';
import { DeviceModeState, VitalSignsStatus } from '@redux/person/personTypes';
import darkGreenDot from 'assets/images/darkGreenDot.png';
import grayDot from 'assets/images/grayDot.png';
import purpleDot from 'assets/images/purpleDot.png';
import { DeviceIcon } from 'components/Icons';
import { TextVariant } from 'components/TextField/TextField';
import { Text } from 'components/Typography';
import { VitalSign } from 'components/VitalSign/VitalSign';
import { PatientMenu, PatientMenuProps } from 'containers/AllPatients/components/PatientMenu/PatientMenu';
import { TableCellValueType } from 'types/global';
import { Color } from 'utils/color';
import { PatientPresenceType } from 'utils/patientPresence';
import { getElapsedTimeDisplay, getDateOnlyDisplayIgnoreTimezone } from 'utils/time';
import { getDisplayValueOrDefault, getInitials, properCase } from 'utils/util';
import { PatientTableColName } from './PatientTableTypes';
import style from './TableCell.scss';
import { getDeviceModeValue } from 'utils/device';

export type PatientTableCellProp<T extends TableCellValueType> = {
  value?: T;
  email?: string;
  colName: PatientTableColName;
  avatarUrl?: string;
  modifiedDate?: string;
  status?: VitalSignsStatus;
  containerClassName?: string;
  hasPatientCreatePermission?: boolean;
} & Partial<PatientMenuProps>;

type ValueTextElementProps = {
  value: string;
  cellTextClassName?: string;
  textVariant?: TextVariant;
};

const ValueTextElement = ({ value = '', cellTextClassName = style.cellText, textVariant = 'sm' }: ValueTextElementProps) => {
  return (
    <Text variant={textVariant} className={cellTextClassName}>
      {value}
    </Text>
  );
};

const getDeviceStatusStyleClassName = (value: string) => {
  const capitalized = value.toUpperCase();
  if (['ONLINE', 'HEALTHY'].includes(capitalized)) return { divClassName: style.deviceStatusActive, textClassName: style.cellTextDeviceStatusActive };
  if (capitalized === 'OFFLINE') return { divClassName: style.deviceStatusOffline, textClassName: style.cellTextDeviceStatusOffline };
  if (capitalized === 'ERROR') return { divClassName: style.deviceStatusError, textClassName: style.cellTextDeviceStatusError };
  return { divClassName: style.deviceStatusOffline, textClassName: style.cellTextDeviceStatusOffline };
};

function getCell<T extends TableCellValueType>({ value, email, colName, avatarUrl, modifiedDate, status, onDeletePatient, hasPatientCreatePermission }: PatientTableCellProp<T>) {
  const strValue = '' + value;
  const emailText = email && email.length > 30 ? email.slice(0, 30) + '...' : email;
  const elapsedTime = modifiedDate ? getElapsedTimeDisplay(modifiedDate) : '';
  const dateOnlyDisplay = modifiedDate ? getDateOnlyDisplayIgnoreTimezone(modifiedDate) : '';

  switch (colName) {
    case PatientTableColName.NAME:
      return (
        <>
          <div className={style.avatar}>
            {avatarUrl ? (
              <img src={avatarUrl} alt="avatar" />
            ) : (
              <Text variant="xs" weight={500} className={style.initials}>
                {getInitials(strValue)}
              </Text>
            )}
          </div>
          <div className={style.nameContainer}>
            <ValueTextElement value={strValue} />
            <ValueTextElement value={emailText || ''} cellTextClassName={style.emailText} />
          </div>
        </>
      );
    case PatientTableColName.PATIENT_TYPE: {
      return <ValueTextElement value={getDisplayValueOrDefault(value)} />;
    }
    case PatientTableColName.HEART_RATE:
    case PatientTableColName.BREATHING_RATE:
      return <VitalSign status={status} textValue={getDisplayValueOrDefault(value)} line2TextValue={elapsedTime} />;
    case PatientTableColName.SLEEP_DURATION:
      return <VitalSign status={status} textValue={getDisplayValueOrDefault(value)} line2TextValue={dateOnlyDisplay} />;
    case PatientTableColName.DEVICE_STATUS: {
      const { divClassName, textClassName } = getDeviceStatusStyleClassName(strValue);
      const deviceStatus: string = properCase((value ?? '') as string);
      return (
        <div className={divClassName}>
          <ValueTextElement value={getDisplayValueOrDefault(deviceStatus)} cellTextClassName={textClassName} />
        </div>
      );
    }
    case PatientTableColName.DEVICE_MODE: {
      const { title, icon: Icon } = getDeviceModeValue({ mode: value as DeviceModeState });
      return (
        <div className={style.deviceMode}>
          {Icon && <Icon width={18} height={18} color={Color.GRAY_BLUE} />}
          <ValueTextElement value={getDisplayValueOrDefault(title)} />
        </div>
      );
    }
    case PatientTableColName.DEVICE_NUMBER: {
      return (
        <div className={style.deviceIdStatus}>
          {value && <DeviceIcon width={16} height={16} color={Color.DARK_BLUE_NEW} />}
          <ValueTextElement value={getDisplayValueOrDefault(value)} />
        </div>
      );
    }
    case PatientTableColName.PERSON_PRESENCE: {
      const patientPresence = value as PatientPresenceType;
      return (
        <div>
          <div className={style.patientPresenceStatus}>
            <img
              className={style.dotImg}
              src={patientPresence === PatientPresenceType.NEAR_THE_DEVICE ? darkGreenDot : patientPresence === PatientPresenceType.PRIVACY_ON ? purpleDot : grayDot}
            />
            <ValueTextElement value={patientPresence} />
          </div>
          {patientPresence !== PatientPresenceType.NEAR_THE_DEVICE && (
            <div className={style.patientPresenceTime}>
              <ValueTextElement value={elapsedTime} cellTextClassName={style.timeText} />
            </div>
          )}
        </div>
      );
    }
    case PatientTableColName.SLEEP_EFFICIENCY:
      return <ValueTextElement value={getDisplayValueOrDefault(value, '-', '%')} />;
    case PatientTableColName.BED_EXITS:
    case PatientTableColName.DAY_SLEEP_TIME:
    case PatientTableColName.NIGHT_AWAKE_TIME:
    case PatientTableColName.NIGHT_SLEEP_TIME:
      return <VitalSign status={status} textValue={getDisplayValueOrDefault(value)} line2TextValue={elapsedTime} />;
    case PatientTableColName.DETAILS:
      return <PatientMenu personId={value as string} onDeletePatient={onDeletePatient} hasPatientCreatePermission={hasPatientCreatePermission} />;
    default:
      return <ValueTextElement value={getDisplayValueOrDefault(value)} />;
  }
}

const TableCell = <T extends TableCellValueType>(cellProps: PatientTableCellProp<T>) => {
  return (
    <td className={classNames(style.tableCell, cellProps.containerClassName)}>
      <div className={style.cellBox}>{getCell(cellProps)}</div>
    </td>
  );
};

export default TableCell;
